import { ModelType } from '@/pages/cvc/ModelControl/config';
import { parseCellKey } from '@/pages/cvc/util/matrixKey';
import { MatrixModel } from '@/stores/cvc';
import { getRuntimeConfig } from '@/util/modelCatalog';
import axios, { AxiosResponse } from 'axios';

// Compiled fallback, applied synchronously at module import so any code
// reading axios.defaults.baseURL before the runtime configuration resolves
// still sees a concrete host — never undefined. Actual requests are
// re-targeted by the interceptor below (ISSUE-026).
axios.defaults.baseURL = process.env.REACT_APP_API_HOST as string;
axios.defaults.headers.post['Content-Type'] = 'application/json';

// Runtime endpoint override (ISSUE-026): hold every request until the runtime
// configuration (public/models.json) resolves, then target its apiHost.
// getRuntimeConfig never rejects, always yields a concrete host (runtime
// value or the compiled REACT_APP_API_HOST fallback), and memoizes a single
// fetch — so after boot this await settles on an already-resolved promise.
axios.interceptors.request.use(async (config) => {
  const { apiHost } = await getRuntimeConfig();
  config.baseURL = apiHost;
  return config;
});
export interface AudioMetadataProps {
  props: {
    duration: number;
    sampleRate: number;
    channels: number;
    bitDepth?: number;
    bitRate?: number;
    codec?: string;
  };
}
export interface ResourceResponseData {
  resource_id: string;
  purpose: string;
  upload_url: string;
  message: string;
}
export interface ResourceResponse {
  status: string;
  message: string;
  data: ResourceResponseData;
}
export interface ResourceFileResponse {
  status: string;
  message: string;
  data: {
    resource_id: string;
    name: string;
    original: {
      url: string;
      metadata: AudioMetadataProps;
    };
    transcoded: {
      url: string;
      metadata: AudioMetadataProps;
    }[];
  };
}
export interface JobIdResponse {
  status: string;
  message: string;
  data: {
    data: { job_ids: string[] };
  };
}

/**
 * Read the issued job ids out of a generation response, and refuse anything
 * that is not a list of them.
 *
 * The five `post*` functions below are TYPED as returning `string[]`, but a
 * type is a claim about the server rather than a check on it. A 200 carrying
 * `{"data":{}}` — the shape a backend produces while its response contract is
 * still settling — used to RESOLVE with `undefined`, and every caller reads
 * `if (!jobIds) return`, so the generation silently did nothing: no rows, no
 * acknowledgement, nothing on the console. That is the same silent no-op the
 * rejection handling was added to remove, reached through the success path.
 *
 * Throwing is what puts it back on the one route that reports: `useJobSubmit`
 * catches it, logs the reason and raises the acknowledgement. It also keeps
 * that hook's contract honest — `null` means "the request failed" and can no
 * longer be produced by a request that succeeded.
 */
const readJobIds = (response: JobIdResponse): string[] => {
  const jobIds = response.data?.data?.job_ids;
  if (!Array.isArray(jobIds)) {
    throw new Error(
      `generation response carried no job_ids array (got ${typeof jobIds})`
    );
  }
  return jobIds;
};
export const getUploadInfo = async (
  sessionId: string,
  name: string,
  type: string
) => {
  return (await axios.post(
    'resources',
    {
      name,
      content_type: type,
      purposes: ['playback'],
    },
    { headers: { 'x-session-id': sessionId } }
  )) as AxiosResponse<ResourceResponse>;
};
// Bounded for the same reason as GET /job below, and with the same value
// (ISSUE-057). ISSUE-039 made reconcileJob await the completion handler (today
// `await completeJob(...)`), so this read sits INSIDE the RECONCILE_BATCH_SIZE
// bound: the batch is bounded in COUNT, but until this timeout existed it was
// unbounded in TIME, and one stalled metadata read held its whole batch open
// for as long as the server chose to stall. The value matches
// GET_JOB_TIMEOUT_MS deliberately — the two run in the same batch, against the
// same host, and are the same kind of small JSON read, so a different bound
// would only make the batch's worst-case wait depend on which of the two
// stalled.
//
// DISPOSITION — read as three rules, one per call site, not as one rule about
// timeouts. All three are pinned in useProcessingEvents.test.tsx.
//
//   1. GET /job, in reconcileJob — TRANSIENT. A non-404 failure leaves the job
//      in its non-terminal status, so the next session change scans it again
//      (useProcessingEvents.ts:201-209). A 404 is the exception: the server no
//      longer knows the job, which is terminal-unknown.
//
//   2. getResourceInfo on the LIVE job_done route — TERMINAL on the first
//      failure, with no retry.
//      State the reason carefully, because the blunt version of it — "job_done
//      is never re-delivered" — is not something this repo can support.
//      ISSUE-029's session resumption replays UNACKNOWLEDGED job events, and
//      this client acknowledges nothing: there is no socket `emit` anywhere in
//      src/. Note which way that cuts — under the server's stated rule it
//      argues FOR a replay, not against one, so it is not the reason either.
//      The reason is that a replay is not a trigger this route can be designed
//      around: it is conditional on the SERVER choosing to treat the next
//      reconnect as a resumption. This side does ASK for that — the handshake
//      carries the stored session_id on every connection attempt
//      (WebSocketProvider.tsx:96-100 for the auth callback and :167-170 for
//      extraHeaders, both pinned in WebSocketProvider.test.tsx) — but asking
//      is all it can do. Whether the server resumes rather than re-keys, and
//      whether it then re-sends an event this client has already seen, are
//      answered in wingwing and nowhere in this repo. So this route has no
//      dependable second chance, and a job left non-terminal on it is a row
//      that spins until the tab is closed. completeJob's catch marks it
//      FAILED with the default 'Generating Failed' copy (ISSUE-039).
//
//   3. getResourceInfo on the RECONCILIATION route — TERMINAL as well, but
//      only once RECONCILE_METADATA_ATTEMPTS attempts have failed (ISSUE-076).
//
// Rule 3 is where ISSUE-057 was revisited, so the reasoning is recorded in
// full. ISSUE-057 ruled this route terminal on the FIRST failure and gave a
// real reason: onSessionChanged does re-scan every job not in
// TERMINAL_STATUSES, but a further session change is a reconnect-with-a-new-id
// and is not guaranteed to arrive, so leaving the job non-terminal is not
// obviously safer than failing it. That reason still holds, and the terminal
// ruling it produced is unchanged. What changed is only WHEN terminal is
// reached, which that argument never spoke to.
//
// What made the first failure the wrong moment to reach it: reconcileJob
// issues GET /job and this read back to back, in the same batch, against the
// same host — and rule 1 calls a non-404 failure of the FIRST of those two
// TRANSIENT. One transient blip therefore reached opposite dispositions
// depending only on which of two adjacent calls in the same function it landed
// on, and "a further session change is not guaranteed" is just as true of rule
// 1, which is not written that way. An argument that would equally condemn the
// adjacent branch cannot be what decides this one. The retry removes the split
// without contradicting either rule: the first failure stops being treated as
// proof of a permanent one, and exhaustion is still terminal.
//
// The correlation is the other half. This route runs precisely because a
// connection was lost and re-established, so a request issued here and the
// failure being retried are not independent events — which is an argument for
// attempting again, and also the reason the attempt is FIXED at two rather
// than open-ended: a retry loop is a poor thing to point at a host that has
// just come back.
//
// So terminal is the final disposition on BOTH routes into completeJob, and
// the routes differ only in the attempt count their caller passes
// (useProcessingEvents.ts:165 and :181-184). The retry is a fixed count with a
// fixed delay — never exponential, never jittered, no retry of the retry — and
// it is sequential within a single job, so reconciliation still has at most
// RECONCILE_BATCH_SIZE requests in flight and that bound is untouched
// (ISSUE-022/ISSUE-039). It costs the batch one extra timeout plus one delay
// in the worst case; the PR for ISSUE-076 does that arithmetic.
export const GET_RESOURCE_INFO_TIMEOUT_MS = 10000;
export const getResourceInfo = async (resource_id: string) => {
  return (await axios.get(`/resources/${resource_id}`, {
    timeout: GET_RESOURCE_INFO_TIMEOUT_MS,
  })) as AxiosResponse<ResourceFileResponse>;
};

// Server-side job lifecycle states (wingwing JobStatus).
export type JobStatus =
  | 'created'
  | 'pending'
  | 'processing'
  | 'done'
  | 'failed';
export interface JobResponse {
  status: string;
  data: {
    job_id: string;
    executor_id?: string;
    status: JobStatus;
    request?: Record<string, unknown>;
    result?: { resource_id?: string } & Record<string, unknown>;
    error?: unknown;
    acknowledged?: boolean;
    execution_time?: string;
  };
}
// Reconciliation calls must never hang a result item forever — bound them.
export const GET_JOB_TIMEOUT_MS = 10000;
export const getJob = async (jobId: string) => {
  return (await axios.get(`/job/${jobId}`, {
    timeout: GET_JOB_TIMEOUT_MS,
  })) as AxiosResponse<JobResponse>;
};

interface DataList {
  model_type: string;
  source_resource_id: string;
  target_resource_ids: string[];
  secondary_target_resource_ids: string[];
  parameters: { [key: string]: number | boolean };
}

export const postCvcSpeakerControl = async (
  modelType: ModelType,
  sessionId: string,
  sourceList: string[],
  parameters: { [key: string]: number | boolean }
) => {
  const response = (await axios.post(
    '/cvc',
    {
      data: sourceList.map((item) => {
        return {
          model_type: modelType,
          source_resource_id: item,
          target_resource_ids: [],
          secondary_target_resource_ids: [],
          parameters,
        };
      }),
    },
    {
      headers: {
        'X-Session-Id': sessionId,
      },
    }
  )) as JobIdResponse;
  return readJobIds(response);
};
export const postCvcGenerate = async (
  modelType: ModelType,
  sessionId: string,
  sourceList: string[],
  targetList: (string | string[])[],
  parameters: { [key: string]: number | boolean }
) => {
  const reqData = sourceList.reduce((map, item) => {
    return map.concat(
      targetList.map((target) => {
        return {
          model_type: modelType,
          source_resource_id: item,
          target_resource_ids: typeof target === 'string' ? [target] : target,
          secondary_target_resource_ids: [],
          parameters,
        };
      })
    );
  }, [] as DataList[]);
  const response = (await axios.post(
    '/cvc',
    {
      data: reqData,
    },
    {
      headers: {
        'X-Session-Id': sessionId,
      },
    }
  )) as JobIdResponse;
  return readJobIds(response);
};
export const postCvcInterpolationGenerate = async (
  modelType: string,
  sessionId: string,
  sourceList: string[],
  groupMap: { [key: string]: (string | string[])[] },
  parameters: { [key: string]: number | boolean }
) => {
  const response = (await axios.post(
    '/cvc',
    {
      data: sourceList.reduce((map, item) => {
        const { GroupA = [], GroupB = [] } = groupMap;
        let newMap = map;
        GroupA.forEach((targetA) => {
          newMap = newMap.concat(
            GroupB.map((targetB) => {
              return {
                model_type: modelType,
                source_resource_id: item,
                target_resource_ids:
                  typeof targetA === 'string' ? [targetA] : targetA,
                secondary_target_resource_ids:
                  typeof targetB === 'string' ? [targetB] : targetB,
                parameters,
              };
            })
          );
        });
        return newMap;
      }, [] as DataList[]),
    },
    {
      headers: {
        'X-Session-Id': sessionId,
      },
    }
  )) as JobIdResponse;
  return readJobIds(response);
};

export const postCvcMatrixGenerate = async (
  modelType: string,
  sessionId: string,
  matrix: MatrixModel,
  parameters: { [key: string]: number | boolean }
) => {
  const response = (await axios.post(
    '/cvc',
    {
      data: Object.keys(matrix)
        .filter((m) => matrix[m])
        .map((key) => {
          // `key.split(':')` destructured to two names silently DISCARDED a
          // third segment, so a resource id containing the delimiter produced
          // a truncated `source_resource_id` and a wrong
          // `target_resource_ids` — a silently wrong conversion sent to the
          // backend, not a visible failure (ISSUE-068).
          //
          // Throwing is deliberate. The frontend cannot repair an ambiguous
          // key, and converting the WRONG audio is worse than not converting:
          // this fails at dispatch, before any request is issued, so nothing
          // half-happens. The rejection is caught by `useJobSubmit` at the one
          // call site (GeneratePanel's GRID branch), which acknowledges it to
          // the user and logs the reason; it no longer escapes to the global
          // async error sink (ISSUE-040), which only ever recorded it
          // redacted.
          const pair = parseCellKey(key);
          if (!pair) {
            throw new Error(
              `matrix key is not a single source:target pair: ${key}`
            );
          }
          return {
            model_type: modelType,
            source_resource_id: pair.sourceId,
            target_resource_ids: [pair.targetId],
            secondary_target_resource_ids: [],
            parameters,
          };
        }),
    },
    {
      headers: {
        'X-Session-Id': sessionId,
      },
    }
  )) as JobIdResponse;
  return readJobIds(response);
};

export const postMssGenerate = async (
  sessionId: string,
  parameters: { [key: string]: number }[]
) => {
  const response = (await axios.post(
    '/mss',
    {
      data: parameters,
    },
    {
      headers: {
        'X-Session-Id': sessionId,
      },
    }
  )) as JobIdResponse;
  return readJobIds(response);
};

export const upload = async (upload_url: string, file: File) => {
  await axios.put(upload_url as string, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

export interface TriggerResponse {
  status: string;
  message: string;
}
// ISSUE-021 (FR-081): signals upload completion after a successful presigned
// PUT so transcoding starts without an object-storage event pipeline.
export const triggerResource = async (resource_id: string) => {
  return (await axios.post(
    `/resources/${resource_id}/trigger`
  )) as AxiosResponse<TriggerResponse>;
};
